import styled from '@emotion/styled'
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { useScrollTop } from 'src/components/table'
import { Colors } from 'src/components/theme'
import { rgbaStrOpacity } from 'src/utils/colors'
import { NESTED_FANCY_SCROLLBAR } from 'src/utils/styles'

const Container = styled.div<{
  maxHeight?: number
  minHeight?: number
  showTopShadpw: boolean
  showBottomShadow: boolean
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: ${({ minHeight }) => (minHeight ? `${minHeight}px` : 'auto')};
  max-height: ${({ maxHeight }) => (maxHeight ? `${maxHeight}px` : 'auto')};
  overflow-y: auto;
  ${NESTED_FANCY_SCROLLBAR}
`
const TopShadow = styled.div`
  position: sticky;
  top: -1px;
  width: 100%;
  height: 0;
  z-index: 1;
  ::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 16px;
    background: linear-gradient(180deg, ${rgbaStrOpacity(Colors.neutral800, 0.05)}, transparent);
    border-top: 1px solid ${Colors.border};
  }
`
const BottomScrollShadow = styled.div`
  position: sticky;
  bottom: -1px;
  width: 100%;
  height: 0;

  ::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 16px;
    background: linear-gradient(0deg, ${rgbaStrOpacity(Colors.neutral800, 0.05)}, transparent);
    border-bottom: 1px solid ${Colors.border};
  }
`

type ScrollableContainerProps = {
  height?: number
  minHeight?: number
  resetScrollWhenChildrenChange?: boolean
  noShadow?: boolean
} & PropsWithChildren
export const ScrollableContainer = (props: ScrollableContainerProps) => {
  const { height, minHeight, children, resetScrollWhenChildrenChange, noShadow } = props
  const tableRef = useRef<HTMLDivElement>(null)
  const [{ scrollEnd }, setScrollConfig] = useState<{
    scrollEnd: boolean
    scrollHeight: number
  }>({
    scrollEnd: false,
    scrollHeight: 0,
  })
  const { scrollTop, onScroll } = useScrollTop(false)

  const onScrollTopChange = useCallback(() => {
    if (!tableRef.current) {
      return
    }
    const top = scrollTop || tableRef.current.scrollTop
    const offsetHeight = tableRef.current.offsetHeight
    const scrollHeight = tableRef.current.scrollHeight

    const scrollEnd = Math.floor(scrollHeight - (top + offsetHeight)) < 1

    setScrollConfig({
      scrollEnd,
      scrollHeight,
    })
  }, [scrollTop, setScrollConfig, height])

  useEffect(() => {
    onScrollTopChange()
  }, [onScrollTopChange])

  // Scroll back to top when children change
  useEffect(() => {
    if (resetScrollWhenChildrenChange && tableRef.current) {
      tableRef.current.scrollTop = 0
    }
  }, [children])

  return (
    <Container
      ref={tableRef}
      maxHeight={height}
      minHeight={minHeight}
      onScroll={onScroll as (event: React.UIEvent<HTMLDivElement, UIEvent>) => void}
      showTopShadpw={scrollTop > 1}
      showBottomShadow={!scrollEnd}>
      <div>
        {scrollTop > 1 && <TopShadow />}
        {children}
        {!scrollEnd && !noShadow && <BottomScrollShadow />}
      </div>
    </Container>
  )
}
